import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiLock, FiSend, FiClock } = FiIcons;

export default function ChatComposer({
  currentUser,
  mode,
  recipient,
  isSending,
  restriction,
  slowModeSeconds = 0,
  onSend,
  onRequestNickname
}) {
  const [input, setInput] = useState('');
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  if (!currentUser) {
    return (
      <div className="shrink-0 border-t border-gray-200 dark:border-white/5 bg-white dark:bg-[#0a0a0a] p-2 sm:p-3 transition-colors">
        <button
          type="button"
          onClick={onRequestNickname}
          className="w-full rounded-full border border-red-600/20 bg-red-600/5 dark:bg-red-600/10 py-2 sm:py-2.5 text-[11px] sm:text-sm font-bold text-red-600 dark:text-red-500 transition-all hover:bg-red-600 hover:text-white active:scale-95"
        >
          Elige un apodo para chatear
        </button>
      </div>
    );
  }

  const privateDisabled = mode === 'private' && !recipient;
  const moderationDisabled = Boolean(restriction);
  const isSlowModeLocked = mode === 'public' && cooldown > 0;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (moderationDisabled || isSlowModeLocked) return;

    const sent = await onSend(input, mode === 'private' ? recipient : null);
    if (sent) {
      setInput('');
      if (mode === 'public' && slowModeSeconds > 0) {
        setCooldown(slowModeSeconds);
      }
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="shrink-0 border-t border-gray-200 dark:border-white/5 bg-white dark:bg-[#0a0a0a] p-1.5 sm:p-3 transition-colors"
    >
      {restriction && (
        <div className="mb-2 rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2">
          <p className="text-[10px] font-bold text-red-600 dark:text-red-400 sm:text-xs">
            {restriction.action === 'ban' ? 'Has sido bloqueado del chat.' : 'Has sido expulsado temporalmente del chat.'}
          </p>
          {restriction.reason && (
            <p className="mt-0.5 text-[9px] text-red-700/80 dark:text-red-300/70">
              Motivo: {restriction.reason}
            </p>
          )}
        </div>
      )}

      {mode === 'private' && recipient && !restriction && (
        <p className="mb-1 flex items-center gap-1 px-2 text-[9px] text-gray-500">
          <SafeIcon icon={FiLock} size={8} className="text-red-600 dark:text-red-500" /> Privado para {recipient}
        </p>
      )}

      {mode === 'public' && slowModeSeconds > 0 && !restriction && (
        <div className="mb-1 flex items-center justify-between px-2 text-[9px] text-purple-600 dark:text-purple-400 font-medium">
          <span className="flex items-center gap-1">
            <SafeIcon icon={FiClock} size={9} /> Modo Lento Activo ({slowModeSeconds}s)
          </span>
          {cooldown > 0 && <span>Disponible en {cooldown}s</span>}
        </div>
      )}

      <div className="flex items-center gap-1.5 sm:gap-2">
        <input
          type="text"
          value={input}
          maxLength={1000}
          disabled={privateDisabled || moderationDisabled || isSlowModeLocked}
          onChange={(event) => setInput(event.target.value)}
          placeholder={
            moderationDisabled
              ? 'Chat deshabilitado por moderación'
              : isSlowModeLocked
              ? `Espera ${cooldown}s para volver a enviar`
              : privateDisabled
              ? 'Selecciona una persona...'
              : mode === 'private'
              ? `Mensaje a ${recipient}...`
              : 'Escribe un mensaje...'
          }
          className="min-w-0 flex-1 rounded-full border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/5 px-3 py-1.5 text-[11px] text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-600 focus:border-red-600 focus:outline-none disabled:opacity-50 sm:px-4 sm:py-2.5 sm:text-sm transition-colors"
        />
        <button
          type="submit"
          disabled={!input.trim() || !currentUser || isSending || privateDisabled || moderationDisabled || isSlowModeLocked}
          aria-label="Enviar mensaje"
          className="rounded-full bg-red-600 p-1.5 sm:p-2.5 text-white transition-all hover:bg-red-700 active:scale-95 disabled:opacity-50 shrink-0 flex items-center justify-center min-w-[32px] sm:min-w-[38px]"
        >
          {isSlowModeLocked ? (
            <span className="text-[10px] font-bold">{cooldown}s</span>
          ) : (
            <SafeIcon icon={FiSend} size={14} />
          )}
        </button>
      </div>
    </form>
  );
}